import { jwtDecode } from 'jwt-decode';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useRecoilState } from 'recoil';
import { v4 as uuid } from 'uuid';
import { accessTokenAtom, conversationsAtom, frameParamsAtom } from '../../state/atoms';
import { isTelemetryEnabled } from '../../utils/getTelemetrySwitch';
import { GTMTagManagerArgs, GTM_CONSTANTS } from './../../constants';

export interface JwtPayload {
  pi_id?: string;
  sub?: string;
  sessid?: string;
  iss?: string;
}

function useTelemetryLoaded() {
  const [accessToken] = useRecoilState(accessTokenAtom);
  const [conversations] = useRecoilState(conversationsAtom);
  const [frameParams, setFrameParams] = useRecoilState(frameParamsAtom);

  useEffect(() => {
    if (isTelemetryEnabled()) {
      // Global initialize event for telemetry loaded
      TagManager.initialize({
        ...GTMTagManagerArgs,
      });
    }
  }, []);

  useEffect(() => {
    if (isTelemetryEnabled()) {
      if (accessToken) {
        let user_id = null;
        let session_id = null;
        const decodedToken = jwtDecode(accessToken) as JwtPayload;
        const isTypeIES = decodedToken?.iss === 'IES';

        if (isTypeIES) {
          const { sub, sessid } = decodedToken;
          user_id = sub;
          session_id = sessid;
        } else {
          const { pi_id } = decodedToken;
          user_id = pi_id;
          session_id = frameParams.login_session_id;
        }

        if (conversations.data.id) {
          // Adding global parameters on application load
          TagManager.dataLayer({
            dataLayer: {
              app_id: GTM_CONSTANTS.appId,
              app_version: `${process.env.REACT_APP_VERSION}`,
              partner_id: frameParams.partner ?? 'Mastering',
              app_launch_id: frameParams.correlation ?? uuid(), // Correlation ID from Consumer
              app_launch_id_originated_by:
                !!frameParams.correlation && frameParams.partner ? frameParams.partner : 'AITutor', // Correlation ID Originated by Consumer "AITutor/PVS/MathXL:HED/Mastering"
              exercise_id: frameParams.exercise_id ?? uuid(),
            },
          });

          // Adding user parameters on application load
          TagManager.dataLayer({
            dataLayer: {
              person_id: user_id ?? frameParams['person_ID'] ?? null,
              person_id_type: isTypeIES ? GTM_CONSTANTS.typeIES : GTM_CONSTANTS.typePI,
              person_role_code: frameParams['user-role'] ?? 'student',
              login_session_id: session_id ?? `${conversations.data.id}`,
            },
          });
        }

        // Update user information to frame params
        if (frameParams?.person_ID === 'NA') {
          setFrameParams({
            ...frameParams,
            person_ID: user_id ?? frameParams['person_ID'] ?? 'NA',
            login_session_id: session_id ?? 'NA',
            person_id_type: isTypeIES ? GTM_CONSTANTS.typeIES : GTM_CONSTANTS.typePI,
          });
        }
      }
    }
  }, [accessToken, conversations.data.id, frameParams, frameParams.partner, setFrameParams]);
}

export default useTelemetryLoaded;
